import { Button, Dialog, FilterToolbar, notify, SearchInput, Select, StatusPill, TextAreaField, TextField, Toggle, type FilterItem, type PillTone } from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";
import { useConsoleMe } from "../../auth/context";
import { shortDate } from "./CustomersList";
import { useStaff } from "./Signups";

export type Lead = {
  id: string;
  company: string;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  source: string;
  status: "new" | "contacted" | "qualified" | "converted" | "lost";
  ownerUserId: string | null;
  owner: string | null;
  notes: string | null;
  lostReason: string | null;
  convertedOrgId: string | null;
  createdAt: string;
  updatedAt: string;
};
const STATUS: Record<Lead["status"], { label: string; tone: PillTone }> = {
  new: { label: "New", tone: "info" },
  contacted: { label: "Contacted", tone: "neutral" },
  qualified: { label: "Qualified", tone: "success" },
  converted: { label: "Converted", tone: "success" },
  lost: { label: "Lost", tone: "neutral" },
};
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");

/** Sales > Leads: prospects the team is working; converting one creates a prospect account to quote. */
export function LeadsPage() {
  const me = useConsoleMe();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "open";
  const q = params.get("q") ?? "";
  const mine = params.get("mine") === "1";
  const page = Number(params.get("page") ?? "0") || 0;
  const [search, setSearch] = useState(q);
  const set = (patch: Record<string, string | null>) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === "" || (k === "status" && v === "open") || v === "0") n.delete(k);
          else n.set(k, v);
        }
        if (!("page" in patch)) n.delete("page");
        return n;
      },
      { replace: true },
    );
  useEffect(() => {
    const t = setTimeout(() => (search !== q ? set({ q: search.trim() }) : undefined), 250);
    return () => clearTimeout(t);
  }, [search]); // only the typed text drives the debounce
  const query = new URLSearchParams({ status, q, mine: mine ? "1" : "0", page: String(page) });
  const list = useQuery({
    queryKey: ["console", "leads", query.toString()],
    queryFn: () => api<{ leads: Lead[]; hasMore: boolean }>(`/v1/console/leads?${query}`),
    placeholderData: keepPreviousData,
  });
  const [open, setOpen] = useState<Lead | "new" | null>(null);
  const canWrite = me.capabilities.includes("leads:write");
  const items: FilterItem[] = [
    {
      key: "status",
      priority: 1,
      active: status !== "open",
      inline: (
        <Select
          variant="toolbar"
          label="Status"
          value={status}
          onChange={(v) => set({ status: v })}
          options={[{ value: "open", label: "Open" }, { value: "new", label: "New" }, { value: "contacted", label: "Contacted" }, { value: "qualified", label: "Qualified" }, { value: "converted", label: "Converted" }, { value: "lost", label: "Lost" }, { value: "all", label: "All" }]}
        />
      ),
    },
    { key: "mine", priority: 2, active: mine, inline: <Toggle label="My leads" checked={mine} onChange={(on) => set({ mine: on ? "1" : null })} /> },
  ];
  const rows = list.data?.leads ?? [];
  const anyActive = status !== "open" || q !== "" || mine;
  return (
    <section className="ffa-page">
      <header className="ffa-head">
        <div>
          <h1 className="ffa-page__title">Leads</h1>
          <p className="ffa-head__sub">Companies the team is working. Convert a lead to quote it in the quoting center.</p>
        </div>
        {canWrite ? <Button variant="primary" onClick={() => setOpen("new")}>Add lead</Button> : null}
      </header>
      <FilterToolbar
        label="Lead filters"
        search={<SearchInput label="Search company, contact or email" value={search} onChange={setSearch} />}
        items={items}
        anyActive={anyActive}
        onReset={() => {
          setSearch("");
          setParams(new URLSearchParams(), { replace: true });
        }}
      />
      {list.isError ? (
        <div className="ffa-notice" role="alert">
          <span>Couldn&apos;t load leads. {message(list.error)}</span>
          <Button size="sm" onClick={() => void list.refetch()}>Try again</Button>
        </div>
      ) : null}
      <div className="ffx-table-wrap ffa-tablewrap" aria-busy={list.isFetching}>
        <table className="ffx-table ffa-table">
          <caption className="ffx-sr">Leads</caption>
          <thead>
            <tr>
              <th scope="col">Company</th>
              <th scope="col">Contact</th>
              <th scope="col" className="ffa-col-wide">Source</th>
              <th scope="col">Status</th>
              <th scope="col">Owner</th>
              <th scope="col" className="ffa-col-wide">Added</th>
              <th scope="col"><span className="ffx-sr">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {list.isPending ? (
              <tr><td colSpan={7} className="ffx-table__empty">Loading leads...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="ffx-table__empty">{anyActive ? "No leads match these filters." : "No open leads. Add one to start."}</td></tr>
            ) : (
              rows.map((l) => (
                <tr key={l.id}>
                  <td data-label="Company"><span className="ffa-cell-strong ffa-trunc" title={l.company}>{l.company}</span></td>
                  <td data-label="Contact">
                    {l.contactName ?? <span className="ffa-muted">No name</span>}
                    {l.email ? <span className="ffa-muted ffa-block ffa-trunc">{l.email}</span> : null}
                  </td>
                  <td data-label="Source" className="ffa-col-wide ffa-cap">{l.source}</td>
                  <td data-label="Status"><StatusPill tone={STATUS[l.status].tone}>{STATUS[l.status].label}</StatusPill></td>
                  <td data-label="Owner">{l.owner ?? <span className="ffa-muted">Unassigned</span>}</td>
                  <td data-label="Added" className="ffa-col-wide"><time dateTime={l.createdAt}>{shortDate(l.createdAt)}</time></td>
                  <td>
                    {l.status === "converted" && l.convertedOrgId ? (
                      <Link className="ffx-btn ffx-btn--ghost ffx-btn--sm" to={`/customers/${l.convertedOrgId}`}>Account</Link>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setOpen(l)} aria-label={`Open lead ${l.company}`}>Open</Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <nav className="ffa-pager" aria-label="Pages">
        <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => set({ page: String(page - 1) })}>Previous</Button>
        <span className="ffa-muted">Page {page + 1}</span>
        <Button size="sm" variant="ghost" disabled={!list.data?.hasMore} onClick={() => set({ page: String(page + 1) })}>Next</Button>
      </nav>
      {open ? <LeadDialog lead={open === "new" ? null : open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function LeadDialog({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  const me = useConsoleMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const staff = useStaff();
  const [f, setF] = useState({
    company: lead?.company ?? "",
    contactName: lead?.contactName ?? "",
    email: lead?.email ?? "",
    phone: lead?.phone ?? "",
    source: lead?.source ?? "manual",
    ownerUserId: lead?.ownerUserId ?? me.user.id,
    notes: lead?.notes ?? "",
    status: (lead?.status ?? "new") as Lead["status"],
    lostReason: lead?.lostReason ?? "",
  });
  const canWrite = me.capabilities.includes("leads:write");
  const canConvert = canWrite && me.capabilities.includes("customers:write");
  const done = () => void qc.invalidateQueries({ queryKey: ["console", "leads"] });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        company: f.company.trim(),
        contactName: f.contactName.trim() || null,
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        source: f.source.trim() || "manual",
        ownerUserId: f.ownerUserId || null,
        notes: f.notes.trim() || null,
      };
      return lead
        ? api(`/v1/console/leads/${lead.id}`, { method: "PATCH", body: { ...body, status: f.status, ...(f.status === "lost" ? { lostReason: f.lostReason.trim() } : {}) } })
        : api("/v1/console/leads", { method: "POST", body });
    },
    onSuccess: () => {
      done();
      notify.success(lead ? "Lead saved" : "Lead added", f.company.trim());
      onClose();
    },
    onError: (e) => notify.error("Couldn't save the lead", message(e)),
  });
  const convert = useMutation({
    mutationFn: () => api<{ orgId: string; name: string }>(`/v1/console/leads/${lead!.id}/convert`, { method: "POST", body: {} }),
    onSuccess: (r) => {
      done();
      notify.success("Converted to a prospect account", "You can quote it now. No email was sent.");
      onClose();
      navigate(`/quotes/new?${new URLSearchParams({ org: r.orgId, name: r.name })}`);
    },
    onError: (e) => notify.error("Couldn't convert", message(e)),
  });
  const emailOk = f.email.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
  const valid = f.company.trim() !== "" && emailOk && (f.status !== "lost" || f.lostReason.trim() !== "");
  return (
    <Dialog
      open
      onClose={onClose}
      title={lead ? lead.company : "Add a lead"}
      description={lead ? `Added ${shortDate(lead.createdAt)} from ${lead.source}.` : "Who is it, and who on the team owns it?"}
      footer={
        <>
          {lead && canConvert && f.status !== "lost" ? (
            <Button variant="ghost" loading={convert.isPending} onClick={() => convert.mutate()}>Convert to prospect</Button>
          ) : null}
          {canWrite ? (
            <Button variant="primary" disabled={!valid} loading={save.isPending} onClick={() => save.mutate()}>{lead ? "Save" : "Add lead"}</Button>
          ) : (
            <Button variant="ghost" onClick={onClose}>Close</Button>
          )}
        </>
      }
    >
      <div className="ffa-form">
        <TextField label="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} maxLength={160} disabled={!canWrite} />
        <TextField label="Contact name" optional value={f.contactName} onChange={(e) => setF({ ...f, contactName: e.target.value })} maxLength={120} disabled={!canWrite} />
        <TextField label="Email" optional type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} error={emailOk ? undefined : "Check the email address"} disabled={!canWrite} />
        <TextField label="Phone" optional type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} maxLength={40} disabled={!canWrite} />
        <TextField label="Source" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} maxLength={60} hint="For example: website chat, trade show, referral" disabled={!canWrite} />
        <Select label="Owner" value={f.ownerUserId} onChange={(v) => setF({ ...f, ownerUserId: v })} options={[{ value: "", label: "Unassigned" }, ...(staff.data?.staff ?? []).map((s) => ({ value: s.id, label: s.name || s.email }))]} />
        {lead ? (
          <Select label="Status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={[{ value: "new", label: "New" }, { value: "contacted", label: "Contacted" }, { value: "qualified", label: "Qualified" }, { value: "lost", label: "Lost" }]} />
        ) : null}
        {f.status === "lost" ? <TextField label="Why was it lost?" value={f.lostReason} onChange={(e) => setF({ ...f, lostReason: e.target.value })} maxLength={300} /> : null}
        <TextAreaField label="Notes" optional value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} maxLength={4000} disabled={!canWrite} />
      </div>
    </Dialog>
  );
}
